import { Button, ButtonColor, Icon, IconName, IconSize, IconVariant } from "@quortex/front-common"
import { useNavigate } from "@tanstack/react-location"
import { useSuspenseQuery } from "@tanstack/react-query"
import { Handle, type NodeProps, Position } from "@xyflow/react"
import type { AddSegmentModalGenerics } from "@/pages/ContentSteering/Segmentation/SegmentEdition/AddSegmentModal"
import { useSegmentList } from "@/queries/segments"
import { useSegmentationFlowStore } from "@/stores/segmentationFlow"
import { SegmentMode, type SegmentType, useSegmentWizardStore } from "@/stores/segmentWizard"

export type NewNode = {
  parentUuid: string
}

interface Props extends NodeProps {
  data: NewNode
}

const NewSegmentationNode = ({ id, data }: Props) => {
  const setShowEdges = useSegmentationFlowStore(state => state.setShowEdges)
  const parentUuid = data.parentUuid
  const { data: segments } = useSuspenseQuery(useSegmentList())
  const siblings = segments.filter(s => s.parentUuid === parentUuid && !s.isFallback)
  const siblingsCountries = siblings.flatMap(s => s.geolocation?.countries ?? [])
  const siblingsSubdivisions = siblings.flatMap(s => s.geolocation?.subdivisions ?? [])
  const siblingsType = (siblings[0]?.type as SegmentType) ?? "geolocation"

  const { setSegmentWizardState, clearWizard } = useSegmentWizardStore(state => ({
    clearWizard: state.clearWizard,
    setSegmentWizardState: state.setSegmentWizardState
  }))
  const navigate = useNavigate()

  type ValidSiblingType = "device" | "geolocation" | "network"

  const addSiblingText = {
    device: "device",
    geolocation: "geo",
    network: "network"
  } as const

  return (
    <>
      <Handle className="invisible" id="input" position={Position.Left} type="target" />

      {siblingsType !== "protocol" ? (
        <button
          className="group radius-sm z-10 flex h-20 w-52 flex-col justify-center py-sm"
          type="button"
          tabIndex={0}
          onMouseEnter={() => setShowEdges(id, true)}
          onMouseLeave={() => setShowEdges(id, false)}
        >
          <Button
            className="w-fit py-md opacity-0 transition-opacity group-hover:opacity-100"
            color={ButtonColor.SECONDARY}
            data-testid="add-sibling-segment"
            type="button"
            onClick={() => {
              clearWizard()
              setSegmentWizardState({
                mode: SegmentMode.SIBLING,
                parentUuid,
                segmentType: siblingsType,
                siblingsCountryCodes: siblingsCountries,
                siblingsSubdivisionCodes: siblingsSubdivisions
              })
              navigate({
                replace: true,
                search: {
                  modal: "add_segment",
                  step: "defineSegment",
                  view: localStorage.getItem("search.view") || undefined
                } as AddSegmentModalGenerics["Search"]
              })
            }}
          >
            <Icon icon={IconName.PLUS} size={IconSize.MEDIUM} type={IconVariant.SOLID} />
            Add{" "}
            {["geolocation", "device", "network"].includes(siblingsType) &&
              addSiblingText[siblingsType as ValidSiblingType]}{" "}
            segment
          </Button>
        </button>
      ) : (
        <div className="invisible">_</div>
      )}
    </>
  )
}

export default NewSegmentationNode
